import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export interface MFAPolicy {
  requireAll: boolean;
  requireAdmins: boolean;
  requireEmployees: boolean;
  requireVendors: boolean;
  requireRelays: boolean;
  requireClients: boolean;
}

const DEFAULT_POLICY: MFAPolicy = {
  requireAll: false,
  requireAdmins: false,
  requireEmployees: false,
  requireVendors: false,
  requireRelays: false,
  requireClients: false,
};

export const useMFAPolicy = () => {
  const { profile } = useAuth();
  const [policy, setPolicy] = useState<MFAPolicy>(DEFAULT_POLICY);
  const [loading, setLoading] = useState(true);

  const fetchPolicy = useCallback(async () => {
    try {
      const { data } = await supabase
        .from('site_settings')
        .select('key, value')
        .in('key', [
          'mfa_required_all',
          'mfa_required_admins',
          'mfa_required_employees',
          'mfa_required_vendors',
          'mfa_required_relays',
          'mfa_required_clients',
        ]);

      if (data) {
        const map: Record<string, boolean> = {};
        data.forEach((row) => {
          const raw = row.value;
          map[row.key] = raw === true || raw === 'true';
        });
        setPolicy({
          requireAll:       map['mfa_required_all']       ?? false,
          requireAdmins:    map['mfa_required_admins']    ?? false,
          requireEmployees: map['mfa_required_employees'] ?? false,
          requireVendors:   map['mfa_required_vendors']   ?? false,
          requireRelays:    map['mfa_required_relays']    ?? false,
          requireClients:   map['mfa_required_clients']   ?? false,
        });
      }
    } catch {
      // En cas d'erreur, pas de MFA requis par défaut
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPolicy();
  }, [fetchPolicy]);

  /** Retourne true si le profil courant doit passer le MFA */
  const requiresMFA = useCallback((): boolean => {
    if (!profile) return false;
    if (policy.requireAll) return true;

    const role = profile.system_role;
    if ((role === 'admin' || role === 'super_admin') && policy.requireAdmins) return true;
    if (role === 'hr'     && policy.requireEmployees) return true;
    if (role === 'vendor' && policy.requireVendors)   return true;
    if (role === 'relay'  && policy.requireRelays)    return true;
    if (role === 'client' && policy.requireClients)   return true;

    return false;
  }, [profile, policy]);

  return { policy, loading, requiresMFA, refetch: fetchPolicy };
};
